import SelectRobotModal from '../../Robot/RobotView/RobotLeftBlock/RobotDetails/RobotMissionMainStatus/SelectRobotModal/SelectRobotModal'
import ListAltIcon from '@mui/icons-material/ListAlt'
import MapIcon from '@mui/icons-material/Map'
import { RobotRead } from 'api/schema'
import { CleaningZoneStatus, TransferRouteStatus } from 'api/schema/index'
import clsx from 'clsx'
import Box from 'components/Box/Box'
import { Button } from 'components/Button/Button'
import CommonButton from 'components/Button/ButtonTypes/CommonButton'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import RobotTypeLabel from 'components/RobotTypeLabel/RobotTypeLabel'
import TextFieldDescribed from 'components/TextFieldDescribed/TextFieldDescribed'
import Title from 'components/Title/Title'
import { MultiLineString, Polygon } from 'geojson'
import { useIsMobile } from 'hooks/useIsMobile'
import { useSuppressNotificationSandwich } from 'hooks/useSuppressNotificationSandwich'
import { ReactComponent as DrawZoneSvg } from 'images/icons/cleaning_zone_small.svg'
import { ReactComponent as RestrictedZoneSvg } from 'images/icons/draw_zone_restricted.svg'
import { ReactComponent as GearSvg } from 'images/icons/gear.svg'
import { ReactComponent as MappingSvg } from 'images/icons/mapping.svg'
import { ReactComponent as RoutingSvg } from 'images/icons/routing.svg'
import { ReactComponent as SatelliteSvg } from 'images/icons/satellite.svg'
import { ReactComponent as RouteFinishSvg } from 'images/icons/transfer_route_finish.svg'
import { ReactComponent as RouteStartSvg } from 'images/icons/transfer_route_start.svg'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { MissionActions } from 'store/actions/mission'
import { RobotActions } from 'store/actions/robot'
import {
  selectCleaningRoutes,
  selectCleaningZones,
  selectMapMode,
  selectMission,
  selectMissionCalculatingStatus,
  selectMissionEditingZone,
  selectMissionFinishPoint,
  selectMissionPointRoutes,
  selectMissionStartPoint,
  selectRestrictZones,
  selectSavingZoneProgress,
  selectTransferRoutes,
} from 'store/selectors/mission'
import store from 'store/store'
import { missionThunks } from 'store/thunks/mission'
import { robotThunks } from 'store/thunks/robot'
import {
  MISSION_CALCULATING_STATUSES,
  MISSION_MAP_DRAWING_MODES,
  MISSION_PAGE_MODES,
  MISSION_ZONE_TYPES,
  PROGRESS_TYPES,
  TIMEOUT_FETCHING_STATUS,
} from 'utils/constants'
import { checkRestrictZoneIntersection } from 'utils/intersectionUtils'
import { isComboOrOmniRobotType } from 'utils/robotTypeDisplayName'
import { NOTIFICATION_SANDWICH_SUPPRESS, isMissionMapPointerActive } from 'utils/notificationSandwichGate'
import { VENDOR } from 'utils/vendorDisplayName'

import CalculatingLoader from './CalculatingLoader/CalculatingLoader'
import MissionButtons from './MissionButtons/MissionButtons'
import MissionSettingsModal from './MissionButtons/MissionSettingsModal/MissionSettingsModal'
import MissionMap from './MissionMap/MissionMap'
import DrawZoneMode from './Modes/DrawZone/DrawZoneMode'
import ViewZonesMode from './Modes/ViewZone/ViewZonesMode'

import style from './MissionView.module.css'
import MapTableToggleStyle from 'styles/MapTableToggle.module.css'
import pageLayoutStyle from 'styles/pageLayout.module.css'

const MissionView = () => {
  const mission = useSelector(selectMission)
  const cleaningZones = useSelector(selectCleaningZones)
  const restrictZones = useSelector(selectRestrictZones)
  const calculatingStatus = useSelector(selectMissionCalculatingStatus)
  const editingZone = useSelector(selectMissionEditingZone)
  const dispatch = useDispatch()

  const [ready, setReady] = useState<[number, number, number, number]>([0, 0, 0, 0]) // OverallCleaning, ReadyCleaning, OverallTransfer, ReadyTransfer
  const [calculationMode, setCalculationMode] = useState<'all' | 'single' | undefined>(undefined)
  const [calculationIsActive, setCalculationIsActive] = useState<boolean>(false)
  const [hideCleaningType, setHideCleaningType] = useState<boolean>(
    !isComboOrOmniRobotType(mission.missionInfo?.robot_type)
  )
  const { t } = useTranslation()
  const [selectedRobot, setSelectedRobot] = useState<RobotRead | undefined>(undefined)
  const [showRobotModal, setShowRobotModal] = useState(false)
  const missionStartPoint = useSelector(selectMissionStartPoint)
  const missionFinishPoint = useSelector(selectMissionFinishPoint)
  const cleaningRoutes = useSelector(selectCleaningRoutes)
  const transferRoutes = useSelector(selectTransferRoutes)
  const missionPointRoutes = useSelector(selectMissionPointRoutes)
  const mapMode = useSelector(selectMapMode)
  const savingZoneProgress = useSelector(selectSavingZoneProgress)
  const isMobile = useIsMobile()
  const [showSidebar, setShowSidebar] = useState(true)

  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [pendingSaveParams, setPendingSaveParams] = useState<{
    zoneName: string
    zoneDescription: string
    zoneType: MISSION_ZONE_TYPES
    cleaningSpeed?: number
    transportSpeed?: number
    isBorderCleaning?: boolean
    equipment_setup?: { turbine_speed?: number; front_dust_suppression?: boolean }
    setup?: {
      swath_sequence_mode?: number
      borders_num?: number
      main_thresholds?: { max_edge_angle?: number; max_edge_angle_for_borders?: number }
    }
    isEdit: boolean
  } | null>(null)

  const isDrawingMode = useMemo(() => {
    return mission.mode !== MISSION_PAGE_MODES.VIEW_ZONES
  }, [mission.mode])

  useSuppressNotificationSandwich(
    isMissionMapPointerActive(mission.mode, mapMode),
    NOTIFICATION_SANDWICH_SUPPRESS.MISSION_MAP_EDIT
  )

  const clearTempData = useCallback(() => {
    dispatch(MissionActions.setTempLaunchPoint(undefined))
    dispatch(MissionActions.setTempZoneRoute(undefined))
    dispatch(MissionActions.setTempZone(undefined))
  }, [dispatch])

  const onAddStartPoint = useCallback(() => {
    clearTempData()
    dispatch(MissionActions.setMode(MISSION_PAGE_MODES.VIEW_ZONES))
    dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.ADD_MISSION_START_POINT))
  }, [clearTempData, dispatch])

  const onAddRoute = useCallback(() => {
    clearTempData()
    dispatch(MissionActions.setDrawingZoneType(MISSION_ZONE_TYPES.RECORDED))
    dispatch(MissionActions.setMode(MISSION_PAGE_MODES.DRAW_ROUTE))
    dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.DRAW_ROUTE))
  }, [clearTempData, dispatch])

  const onAddCleaningZone = useCallback(() => {
    clearTempData()
    dispatch(MissionActions.setDrawingZoneType(MISSION_ZONE_TYPES.CLEANING))
    dispatch(MissionActions.setMode(MISSION_PAGE_MODES.DRAW_ZONE))
    dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.DRAW_POLYGON))
  }, [clearTempData, dispatch])

  const onAddRestrictZone = useCallback(() => {
    clearTempData()
    dispatch(MissionActions.setDrawingZoneType(MISSION_ZONE_TYPES.RESTRICT))
    dispatch(MissionActions.setMode(MISSION_PAGE_MODES.DRAW_ZONE))
    dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.DRAW_POLYGON))
  }, [clearTempData, dispatch])

  const onAddFinishPoint = useCallback(() => {
    clearTempData()
    dispatch(MissionActions.setMode(MISSION_PAGE_MODES.VIEW_ZONES))
    dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.ADD_MISSION_FINISH_POINT))
  }, [clearTempData, dispatch])

  const [isSettingsOpen, setIsSettingsOpen] = useState(false)

  useEffect(() => {
    // When the component mounts or unmounts, clear the robot from the Redux store
    // to prevent its position from being shown if navigated from MissionControl.
    dispatch(RobotActions.setRobotInfo(undefined))
    return () => {
      dispatch(RobotActions.setRobotInfo(undefined))
    }
  }, [dispatch])

  useEffect(() => {
    if (mission.geoTiffLayer?.layer) {
      dispatch(MissionActions.setShowGeoTiffLayer(true))
      dispatch(RobotActions.setShowLidarMap(false))
    }
  }, [dispatch, mission.geoTiffLayer?.layer])

  useEffect(() => {
    const fetchData = async () => {
      if (mission.missionInfo) {
        try {
          const response = await store.dispatch(missionThunks.fetchCalculationStatus(mission.missionInfo?.id))
          if (!response) return

          const overallCleaning = response.cleaning_zones_statuses?.length ?? 0
          const overallTransfers = overallCleaning > 0 ? overallCleaning - 1 : 1
          let readyCleaning = 0
          let readyTransfer = 0

          // An additional logic to control calculationMode state.
          // It needs for the correct CalculatingLoader view displaying
          if (
            !calculationIsActive &&
            response.status === MISSION_CALCULATING_STATUSES.CALCULATING &&
            calculationMode !== undefined
          ) {
            setCalculationIsActive(true)
          }

          // Get readyCleaning count
          if (response.cleaning_zones_statuses.length > 0) {
            const cleaningReadyCount: number = response.cleaning_zones_statuses.filter(
              (zone: CleaningZoneStatus) => zone.status === 'ok'
            ).length
            readyCleaning += cleaningReadyCount
          }
          // Get readyTransfer count
          if (response.transfer_routes_statuses.length > 0) {
            const transferReadyCount: number = response.transfer_routes_statuses.filter(
              (zone: TransferRouteStatus) => zone.status === 'ok'
            ).length
            readyTransfer += transferReadyCount
          }
          setReady([overallCleaning, readyCleaning, overallTransfers, readyTransfer])

          // Update page case
          if (response.status === MISSION_CALCULATING_STATUSES.CALCULATING && calculationMode === undefined) {
            if (
              response.cleaning_zones_statuses.filter((zone: CleaningZoneStatus) => zone.status === 'calculating')
                .length > 1
            ) {
              setCalculationMode('all')
            } else {
              setCalculationMode('single')
            }
          }

          // Reset CalculationMode
          if (calculationIsActive && response.status !== MISSION_CALCULATING_STATUSES.CALCULATING) {
            setCalculationIsActive(false)
            setCalculationMode(undefined)
          }
        } catch (error) {
          console.log('Unable to get mission status')
        }
      }
    }

    fetchData() // Fetch data initially

    const interval = setInterval(fetchData, TIMEOUT_FETCHING_STATUS)

    return () => clearInterval(interval)
    // missionId — avoid restarting interval on every mission update (setZonesStatus gives new ref and caused request loop)
  }, [mission.missionInfo?.id, calculatingStatus, calculationIsActive, calculationMode])

  const onSelectRobotClick = () => {
    if (selectedRobot) {
      setSelectedRobot(undefined)
      dispatch(RobotActions.setRobotInfo(undefined))
      return
    }
    setShowRobotModal(true)
  }
  const onRobotSelected = async (robot: RobotRead | undefined) => {
    setShowRobotModal(false)
    setSelectedRobot(robot)
    if (robot) {
      store.dispatch(robotThunks.fetchRobot(robot.id))
    }
  }

  useEffect(() => {
    setHideCleaningType(!isComboOrOmniRobotType(mission.missionInfo?.robot_type))
  }, [mission.missionInfo])

  const proceedSaveZone = async (
    zoneName: string,
    zoneDescription: string,
    zoneType: MISSION_ZONE_TYPES,
    cleaningSpeed?: number,
    transportSpeed?: number,
    isBorderCleaning?: boolean,
    equipment_setup?: { turbine_speed?: number; front_dust_suppression?: boolean; scrubber_brush_speed?: number },
    setup?: {
      swath_sequence_mode?: number
      borders_num?: number
      main_thresholds?: { max_edge_angle?: number; max_edge_angle_for_borders?: number }
    },
    keepEditing?: boolean
  ) => {
    if (!mission.tempZone) {
      return
    }
    if (zoneType === MISSION_ZONE_TYPES.CLEANING || zoneType === MISSION_ZONE_TYPES.RECORDED) {
      await store.dispatch(
        missionThunks.createCleaningZone(
          zoneName,
          zoneDescription,
          mission.tempZone.geometry as Polygon,
          mission.tempZone.properties.route_id,
          mission.tempLaunchPoint?.geometry,
          cleaningSpeed ?? mission.missionInfo?.cleaning_speed ?? 0.5,
          transportSpeed ?? mission.missionInfo?.transport_speed ?? 0.7,
          isBorderCleaning,
          mission.tempZoneRoute?.geometry as MultiLineString,
          equipment_setup,
          zoneType === MISSION_ZONE_TYPES.RECORDED,
          setup,
          mission.tempZoneRoute?.properties?.backwardGeometry as MultiLineString
        )
      )
    } else if (zoneType === MISSION_ZONE_TYPES.RESTRICT) {
      await store.dispatch(
        missionThunks.createRestrictZone(zoneName, zoneDescription, mission.tempZone.geometry as Polygon)
      )
    }
    if (mission.tempZoneRoute) {
      await store.dispatch(missionThunks.fetchMissionBackground(mission.missionInfo?.id || -1))
    }
  }

  const onSaveZoneClicked = async (
    zoneName: string,
    zoneDescription: string,
    zoneType: MISSION_ZONE_TYPES,
    cleaningSpeed?: number,
    transportSpeed?: number,
    isBorderCleaning?: boolean,
    equipment_setup?: { turbine_speed?: number; front_dust_suppression?: boolean; scrubber_brush_speed?: number },
    setup?: {
      swath_sequence_mode?: number
      borders_num?: number
      main_thresholds?: { max_edge_angle?: number; max_edge_angle_for_borders?: number }
    },
    keepEditing?: boolean
  ) => {
    if (!mission.tempZone || isConfirmOpen || savingZoneProgress === PROGRESS_TYPES.WORK) {
      return
    }
    if (zoneType === MISSION_ZONE_TYPES.RESTRICT) {
      const intersects = checkRestrictZoneIntersection(
        mission.tempZone.geometry as Polygon,
        cleaningRoutes,
        transferRoutes,
        missionPointRoutes
      )
      if (intersects) {
        setPendingSaveParams({
          zoneName,
          zoneDescription,
          zoneType,
          cleaningSpeed,
          transportSpeed,
          isBorderCleaning,
          equipment_setup,
          setup,
          isEdit: false,
        })
        setIsConfirmOpen(true)
        return
      }
    }

    await proceedSaveZone(
      zoneName,
      zoneDescription,
      zoneType,
      cleaningSpeed,
      transportSpeed,
      isBorderCleaning,
      equipment_setup,
      setup
    )
  }

  const proceedSaveChangedZone = async (
    zoneName: string,
    zoneDescription: string,
    zoneType: MISSION_ZONE_TYPES,
    cleaningSpeed?: number,
    transportSpeed?: number,
    isBorderCleaning?: boolean,
    equipment_setup?: { turbine_speed?: number; front_dust_suppression?: boolean; scrubber_brush_speed?: number },
    setup?: {
      swath_sequence_mode?: number
      borders_num?: number
      main_thresholds?: { max_edge_angle?: number; max_edge_angle_for_borders?: number }
    },
    keepEditing?: boolean
  ) => {
    if (!mission.tempZone) {
      return
    }
    if (zoneType === MISSION_ZONE_TYPES.CLEANING || zoneType === MISSION_ZONE_TYPES.RECORDED) {
      await store.dispatch(
        missionThunks.editCleaningZone(
          mission.tempZone.properties?.zone_id,
          zoneName,
          zoneDescription,
          mission.tempZone.geometry as Polygon,
          mission.tempZoneRoute?.geometry as MultiLineString,
          mission.tempZone.properties.route_id,
          mission.tempLaunchPoint?.geometry,
          cleaningSpeed,
          transportSpeed,
          isBorderCleaning,
          equipment_setup,
          setup,
          keepEditing
        )
      )
    } else {
      await store.dispatch(
        missionThunks.editRestrictZone(
          mission.tempZone.properties?.zone_id,
          zoneName,
          zoneDescription,
          mission.tempZone.geometry as Polygon
        )
      )
    }
  }

  const onSaveChangedZoneClicked = async (
    zoneName: string,
    zoneDescription: string,
    zoneType: MISSION_ZONE_TYPES,
    cleaningSpeed?: number,
    transportSpeed?: number,
    isBorderCleaning?: boolean,
    equipment_setup?: { turbine_speed?: number; front_dust_suppression?: boolean; scrubber_brush_speed?: number },
    setup?: {
      swath_sequence_mode?: number
      borders_num?: number
      main_thresholds?: { max_edge_angle?: number; max_edge_angle_for_borders?: number }
    },
    keepEditing?: boolean
  ) => {
    if (!mission.tempZone || isConfirmOpen || savingZoneProgress === PROGRESS_TYPES.WORK) {
      return
    }
    if (zoneType === MISSION_ZONE_TYPES.RESTRICT) {
      const intersects = checkRestrictZoneIntersection(
        mission.tempZone.geometry as Polygon,
        cleaningRoutes,
        transferRoutes,
        missionPointRoutes
      )
      if (intersects) {
        setPendingSaveParams({
          zoneName,
          zoneDescription,
          zoneType,
          cleaningSpeed,
          transportSpeed,
          isBorderCleaning,
          equipment_setup,
          setup,
          isEdit: true,
        })
        setIsConfirmOpen(true)
        return
      }
    }

    await proceedSaveChangedZone(
      zoneName,
      zoneDescription,
      zoneType,
      cleaningSpeed,
      transportSpeed,
      isBorderCleaning,
      equipment_setup,
      setup,
      keepEditing
    )
  }

  const handleConfirmSave = async () => {
    if (!pendingSaveParams) {
      return
    }
    const {
      zoneName,
      zoneDescription,
      zoneType,
      cleaningSpeed,
      transportSpeed,
      isBorderCleaning,
      equipment_setup,
      setup,
      isEdit,
    } = pendingSaveParams
    // Close first so Keep cannot leave a stuck dialog if the user saves again
    // while the zone request is still in flight.
    setIsConfirmOpen(false)
    setPendingSaveParams(null)
    if (isEdit) {
      await proceedSaveChangedZone(
        zoneName,
        zoneDescription,
        zoneType,
        cleaningSpeed,
        transportSpeed,
        isBorderCleaning,
        equipment_setup,
        setup
      )
    } else {
      await proceedSaveZone(
        zoneName,
        zoneDescription,
        zoneType,
        cleaningSpeed,
        transportSpeed,
        isBorderCleaning,
        equipment_setup,
        setup
      )
    }
  }
  const onSaveChangedZoneRouteClicked = async (
    zoneName: string,
    zoneDescription: string,
    zoneType: MISSION_ZONE_TYPES,
    cleaningSpeed?: number,
    transportSpeed?: number,
    isBorderCleaning?: boolean,
    equipment_setup?: { turbine_speed?: number; front_dust_suppression?: boolean },
    setup?: {
      swath_sequence_mode?: number
      borders_num?: number
      main_thresholds?: { max_edge_angle?: number; max_edge_angle_for_borders?: number }
    }
  ) => {
    if (!mission.tempZoneRoute || !editingZone || !mission.tempZone) {
      console.log('No temp route or zone')
      return
    }
    await store.dispatch(
      missionThunks.editCleaningZone(
        editingZone.properties?.zone_id,
        zoneName,
        zoneDescription,
        mission.tempZone?.geometry as Polygon,
        mission.tempZoneRoute.geometry as MultiLineString,
        mission.tempZone.properties.route_id,
        mission.tempLaunchPoint?.geometry,
        cleaningSpeed,
        transportSpeed,
        isBorderCleaning,
        equipment_setup,
        setup,
        false,
        mission.tempZoneRoute?.properties?.backwardGeometry as MultiLineString
      )
    )
    await store.dispatch(missionThunks.fetchMissionBackground(mission.missionInfo?.id || -1))
  }

  const modeList = {
    [`${MISSION_PAGE_MODES.DRAW_ZONE}`]: <DrawZoneMode onSaveClicked={onSaveZoneClicked} />,
    [`${MISSION_PAGE_MODES.VIEW_ZONES}`]: (
      <ViewZonesMode
        cleaningZones={cleaningZones}
        restrictZones={restrictZones}
        setCalculationMode={setCalculationMode}
        hideCleaningType={hideCleaningType}
      />
    ),
    [`${MISSION_PAGE_MODES.DRAW_ROUTE}`]: <DrawZoneMode onSaveClicked={onSaveZoneClicked} />,
    [`${MISSION_PAGE_MODES.EDIT_ZONE}`]: <DrawZoneMode onSaveClicked={onSaveChangedZoneClicked} zone={editingZone} />,
    [`${MISSION_PAGE_MODES.EDIT_ZONE_ROUTE}`]: (
      <DrawZoneMode onSaveClicked={onSaveChangedZoneRouteClicked} zone={editingZone} />
    ),
    [`${MISSION_PAGE_MODES.ADJUST_COVERAGE}`]: (
      <DrawZoneMode
        onSaveClicked={onSaveChangedZoneClicked}
        zone={editingZone}
        coverageOnly={true}
        setCalculationMode={setCalculationMode}
      />
    ),
  }

  return (
    <div className={style.container}>
      <CalculatingLoader
        visible={calculatingStatus === MISSION_CALCULATING_STATUSES.CALCULATING && calculationMode !== undefined}
        ready={ready}
        mode={calculationMode}
      />
      <div className={clsx(style.main, { [style.mainMobile]: isMobile })}>
        <div className={style.headerArea}>
          <div className={style.titleBlock}>
            <div className={style.titleContent}>
              <div className={style.nameRow}>
                <Title text={mission.missionInfo?.name || ''} className={style.mobileTitle} />
                <div className={style.titleId}>
                  {t('missions.id')}: {mission.missionInfo?.id}
                </div>
                <div className={style.titleId} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {t('missions.localization') || 'Localization'}:{' '}
                  {mission.missionInfo?.location ? (
                    <>
                      SLAM
                      <MappingSvg width={18} height={18} className={style.themedIcon} title="SLAM" />
                    </>
                  ) : (
                    <>
                      GNSS
                      <SatelliteSvg width={18} height={18} className={style.themedIcon} title="GNSS" />
                    </>
                  )}
                </div>
                {!isMobile && <RobotTypeLabel vendor={VENDOR.AUTONOMICS} robotType={mission.missionInfo?.robot_type} />}
              </div>
            </div>
            <div className={style.selectBlock}>
              <div className={style.maintenanceButtonsGroup}>
                <button
                  className={clsx(style.maintenanceButton, {
                    [style.maintenanceButtonActive]:
                      mapMode === MISSION_MAP_DRAWING_MODES.ADD_MISSION_START_POINT ||
                      mapMode === MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_MISSION_POINT,
                  })}
                  title={t('mission.add_start_point')}
                  onClick={onAddStartPoint}
                  disabled={isDrawingMode || !!missionStartPoint}
                >
                  <RouteStartSvg width={24} height={24} />
                </button>
                <button
                  className={clsx(style.maintenanceButton, {
                    [style.maintenanceButtonActive]:
                      mission.mode === MISSION_PAGE_MODES.DRAW_ROUTE ||
                      mapMode === MISSION_MAP_DRAWING_MODES.DRAW_ROUTE,
                  })}
                  title={t('mission.add_route')}
                  onClick={onAddRoute}
                  disabled={isDrawingMode}
                >
                  <RoutingSvg width={24} height={24} />
                </button>
                <button
                  className={clsx(style.maintenanceButton, {
                    [style.maintenanceButtonActive]:
                      (mission.mode === MISSION_PAGE_MODES.DRAW_ZONE ||
                        mission.mode === MISSION_PAGE_MODES.EDIT_ZONE) &&
                      mission.drawingZoneType === MISSION_ZONE_TYPES.CLEANING,
                  })}
                  title="Cleaning Zone"
                  onClick={onAddCleaningZone}
                  disabled={isDrawingMode}
                >
                  <DrawZoneSvg width={24} height={24} />
                </button>
                <button
                  className={clsx(style.maintenanceButton, {
                    [style.maintenanceButtonActive]:
                      (mission.mode === MISSION_PAGE_MODES.DRAW_ZONE ||
                        mission.mode === MISSION_PAGE_MODES.EDIT_ZONE) &&
                      mission.drawingZoneType === MISSION_ZONE_TYPES.RESTRICT,
                  })}
                  title="Restricted Zone"
                  onClick={onAddRestrictZone}
                  disabled={isDrawingMode}
                >
                  <RestrictedZoneSvg width={24} height={24} />
                </button>
                <button
                  className={clsx(style.maintenanceButton, {
                    [style.maintenanceButtonActive]: mapMode === MISSION_MAP_DRAWING_MODES.ADD_MISSION_FINISH_POINT,
                  })}
                  title={t('mission.add_finish_point')}
                  onClick={onAddFinishPoint}
                  disabled={isDrawingMode || !!missionFinishPoint}
                >
                  <RouteFinishSvg width={24} height={24} />
                </button>

                <button className={style.headerButton} title="Settings" onClick={() => setIsSettingsOpen(true)}>
                  <GearSvg />
                </button>
              </div>
              <MissionSettingsModal
                isOpen={isSettingsOpen}
                onClose={() => setIsSettingsOpen(false)}
                missionId={mission.missionInfo?.id || -1}
                missionName={mission.missionInfo?.name || ''}
                locationName={mission.missionInfo?.location}
              />
              <SelectRobotModal
                isOpen={showRobotModal}
                robotType={mission.missionInfo?.robot_type}
                includeVendor={VENDOR.AUTONOMICS}
                showOnline={true}
                onClose={() => setShowRobotModal(false)}
                onConfirmClick={onRobotSelected}
              />
              <ConfirmDialog
                isOpen={isConfirmOpen}
                onClose={() => {
                  setIsConfirmOpen(false)
                  setPendingSaveParams(null)
                }}
                onConfirmClick={handleConfirmSave}
                title={t('mission.restrict_zone_warning_title')}
                confirmText={t('mission.restrict_zone_warning_keep')}
                cancelText={t('mission.restrict_zone_warning_edit')}
              >
                <div style={{ padding: '16px 0', fontSize: '14px', lineHeight: '1.5' }}>
                  {t('mission.restrict_zone_warning_body')}
                </div>
              </ConfirmDialog>
            </div>
          </div>
        </div>
        <div
          className={clsx(style.leftBlock, {
            [style.hidden]: isMobile && !showSidebar,
            [style.leftBlockMobile]: isMobile,
          })}
        >
          <Box className={style.box}>{modeList[mission.mode]}</Box>
        </div>
        <div className={style.mapWrapper}>
          <MissionMap selectedRobot={selectedRobot || undefined} onSelectRobotClick={onSelectRobotClick} />
        </div>
        {isMobile && (
          <button
            className={MapTableToggleStyle.toggle}
            onClick={() => setShowSidebar((prev: boolean) => !prev)}
            title={showSidebar ? 'Switch to Map' : 'Switch to Table'}
            style={{
              position: 'absolute',
              bottom: '24px',
              right: '24px',
            }}
          >
            {showSidebar ? (
              <MapIcon className={MapTableToggleStyle.icon} />
            ) : (
              <ListAltIcon className={MapTableToggleStyle.icon} />
            )}
          </button>
        )}
      </div>
    </div>
  )
}

export default MissionView
